import { Dexie } from 'dexie';

export const db = new Dexie('CareTrackDB');

db.version(1).stores({
    doctors: 'id, email, created_by_doctor_id',
    patients: 'id, treated_by_doctor_id, name',
    appointments: 'id, doctor_id, patient_id, date_time, status',
    facility_charges: 'id, appointment_id, referral_doctor_id, paid_status',
    settings: 'doctor_id',
    sync_queue: '++id, type, entity, status, created_at'
});

// Version 2: Add _synced field for offline-first sync status tracking
db.version(2).stores({
    doctors: 'id, email, created_by_doctor_id, _synced',
    patients: 'id, treated_by_doctor_id, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, _synced',
    facility_charges: 'id, appointment_id, referral_doctor_id, paid_status, _synced',
    settings: 'doctor_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id'
});
// Version 3: Add appointment_notes for text/media attachments
db.version(3).stores({
    doctors: 'id, email, created_by_doctor_id, _synced',
    patients: 'id, treated_by_doctor_id, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, _synced',
    facility_charges: 'id, appointment_id, referral_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, _synced',
    settings: 'doctor_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id'
});

// Version 4: Add phone index to doctors and referral_doctor_id index to patients/facility_charges
db.version(4).stores({
    doctors: 'id, email, phone, created_by_doctor_id, _synced',
    patients: 'id, treated_by_doctor_id, name, referral_doctor_id, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, _synced',
    facility_charges: 'id, appointment_id, referral_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, _synced',
    settings: 'doctor_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id'
});

// Version 5: Add clinic support with role-based access control
db.version(5).stores({
    doctors: 'id, email, phone, created_by_doctor_id, _synced',

    patients: 'id, phone, name, _synced', // Removed treated_by_doctor_id
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, _synced',

    facility_charges: 'id, appointment_id, referral_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, _synced',
    settings: 'doctor_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    // New clinic tables
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, doctor_id, [clinic_id+doctor_id], role, _synced',
    patient_doctor_links: 'id, patient_id, doctor_id, clinic_id, _synced'
});

// Version 6: Add treating_doctor_id to facility_charges for role-based filtering
db.version(6).stores({
    doctors: 'id, email, phone, created_by_doctor_id, _synced',

    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, _synced',

    facility_charges: 'id, appointment_id, referral_doctor_id, treating_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, _synced',
    settings: 'doctor_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, doctor_id, [clinic_id+doctor_id], role, _synced',
    patient_doctor_links: 'id, patient_id, doctor_id, clinic_id, _synced'
});

// Version 7: Add commission_rules for decoupled facility_charge logic
db.version(7).stores({
    doctors: 'id, email, phone, created_by_doctor_id, _synced',

    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, _synced',

    facility_charges: 'id, appointment_id, referral_doctor_id, treating_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, _synced',
    settings: 'doctor_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, doctor_id, [clinic_id+doctor_id], role, _synced',
    patient_doctor_links: 'id, patient_id, doctor_id, clinic_id, _synced',
    commission_rules: 'id, doctor_id, patient_id, clinic_id, referral_doctor_id, [doctor_id+patient_id], _synced'
});

// Version 8: Rename Doctors to Users and Add Type
db.version(8).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced', // Renamed from doctors, changed created_by_doctor_id to created_by_user_id
    doctors: null,


    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, _synced',

    facility_charges: 'id, appointment_id, referral_doctor_id, treating_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, _synced',
    settings: 'doctor_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, doctor_id, [clinic_id+doctor_id], role, _synced',
    patient_doctor_links: 'id, patient_id, doctor_id, clinic_id, _synced',
    commission_rules: 'id, doctor_id, patient_id, clinic_id, referral_doctor_id, [doctor_id+patient_id], _synced'
}).upgrade(async trans => {
    // Migrate data from doctors to users
    await trans.doctors.toArray().then(doctors => {
        return trans.users.bulkAdd(doctors.map(d => {
            const { created_by_doctor_id, ...rest } = d;
            return {
                ...rest,
                created_by_user_id: created_by_doctor_id,
                type: 'doctor' // Default all existing records to 'doctor' type
            };
        }));
    });
});

// Version 9: Rename doctor_id to user_id in settings, clinic_members, patient_doctor_links and upgrade tables.
db.version(9).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced',
    doctors: null,
    patients: 'id, treated_by_doctor_id, phone, name, referral_doctor_id, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, _synced',
    facility_charges: 'id, appointment_id, referral_doctor_id, treating_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, _synced',
    settings: null, // Delete old table to allow PK change
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, user_id, [clinic_id+user_id], role, _synced',
    patient_doctor_links: 'id, patient_id, user_id, clinic_id, _synced',
    commission_rules: 'id, doctor_id, patient_id, clinic_id, referral_doctor_id, [doctor_id+patient_id], _synced'
}).upgrade(async trans => {
    // Migrate clinic_members
    await trans.clinic_members.toCollection().modify(m => {
        if (m.doctor_id) {
            m.user_id = m.doctor_id;
            delete m.doctor_id;
        }
    });
    // Migrate patient_doctor_links
    await trans.patient_doctor_links.toCollection().modify(l => {
        if (l.doctor_id) {
            l.user_id = l.doctor_id;
            delete l.doctor_id;
        }
    });
});

// Version 10: Re-create settings with new PK
db.version(10).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced',
    doctors: null,


    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, _synced',

    facility_charges: 'id, appointment_id, referral_doctor_id, treating_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, _synced',
    settings: 'user_id', // Re-created with new PK
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, user_id, [clinic_id+user_id], role, _synced',
    patient_doctor_links: 'id, patient_id, user_id, clinic_id, _synced',
    commission_rules: 'id, user_id, patient_id, clinic_id, referral_doctor_id, [user_id+patient_id], _synced' // Renamed doctor_id to user_id
}).upgrade(async trans => {
    // Migrate commission_rules
    await trans.commission_rules.toCollection().modify(r => {
        if (r.doctor_id) {
            r.user_id = r.doctor_id;
            delete r.doctor_id;
        }
    });
});

// Version 11: Add invoices table for invoice generation feature
db.version(11).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced',
    doctors: null,
    patients: 'id, treated_by_doctor_id, phone, name, referral_doctor_id, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, invoice_id, _synced',  // Added invoice_id
    facility_charges: 'id, appointment_id, referral_doctor_id, treating_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, _synced',
    settings: 'user_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, user_id, [clinic_id+user_id], role, _synced',
    patient_doctor_links: 'id, patient_id, user_id, clinic_id, _synced',
    commission_rules: 'id, user_id, patient_id, clinic_id, referral_doctor_id, [user_id+patient_id], _synced',
    // New invoices table
    invoices: 'id, invoice_number, doctor_id, clinic_id, patient_id, status, _synced'
});


// Version 12: Add subscription plans and add-ons
db.version(12).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced',
    doctors: null,


    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, invoice_id, _synced',

    facility_charges: 'id, appointment_id, referral_doctor_id, treating_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, _synced',
    settings: 'user_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, user_id, [clinic_id+user_id], role, _synced',
    patient_doctor_links: 'id, patient_id, user_id, clinic_id, _synced',
    commission_rules: 'id, user_id, patient_id, clinic_id, referral_doctor_id, [user_id+patient_id], _synced',
    invoices: 'id, invoice_number, doctor_id, clinic_id, patient_id, status, _synced',
    // New subscription tables
    plans: 'id',
    user_subscriptions: 'id, user_id, plan_id, is_active',
    user_addons: 'id, user_id, addon_id',
    addons: 'id'
});

// Version 13: Add cloud media storage fields to appointment_notes
db.version(13).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced',
    doctors: null,


    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, invoice_id, _synced',

    facility_charges: 'id, appointment_id, referral_doctor_id, treating_doctor_id, paid_status, _synced',
    // Updated appointment_notes with cloud media fields: media_key, file_size_bytes, upload_status
    appointment_notes: 'id, appointment_id, doctor_id, type, upload_status, _synced',
    settings: 'user_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, user_id, [clinic_id+user_id], role, _synced',
    patient_doctor_links: 'id, patient_id, user_id, clinic_id, _synced',
    commission_rules: 'id, user_id, patient_id, clinic_id, referral_doctor_id, [user_id+patient_id], _synced',
    invoices: 'id, invoice_number, doctor_id, clinic_id, patient_id, status, _synced',
    plans: 'id',
    user_subscriptions: 'id, user_id, plan_id, is_active',
    user_addons: 'id, user_id, addon_id',
    addons: 'id'
});

// Version 14: Add media_cache table for caching downloaded images/videos
db.version(14).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced',
    doctors: null,


    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, invoice_id, _synced',

    facility_charges: 'id, appointment_id, referral_doctor_id, treating_doctor_id, paid_status, _synced',
    appointment_notes: 'id, appointment_id, doctor_id, type, upload_status, _synced',
    settings: 'user_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, user_id, [clinic_id+user_id], role, _synced',
    patient_doctor_links: 'id, patient_id, user_id, clinic_id, _synced',
    commission_rules: 'id, user_id, patient_id, clinic_id, referral_doctor_id, [user_id+patient_id], _synced',
    invoices: 'id, invoice_number, doctor_id, clinic_id, patient_id, status, _synced',
    plans: 'id',
    user_subscriptions: 'id, user_id, plan_id, is_active',
    user_addons: 'id, user_id, addon_id',
    addons: 'id',
    // Cache for downloaded media (images/videos) indexed by media_key
    // Stores: { media_key, blob, size_bytes, content_type, cached_at }
    media_cache: 'media_key, cached_at'
});

// Version 15: Rename facility_charges to facility_charges, and commission_rules to fee_agreements
db.version(15).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced',
    doctors: null,
    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, invoice_id, _synced',

    facility_charges: 'id, appointment_id, referral_doctor_id, treating_doctor_id, paid_status, _synced',
    commissions: null,

    appointment_notes: 'id, appointment_id, doctor_id, type, upload_status, _synced',
    settings: 'user_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, user_id, [clinic_id+user_id], role, _synced',
    patient_doctor_links: 'id, patient_id, user_id, clinic_id, _synced',

    fee_agreements: 'id, user_id, patient_id, clinic_id, referral_doctor_id, [user_id+patient_id], _synced',
    commission_rules: null,

    invoices: 'id, invoice_number, doctor_id, clinic_id, patient_id, status, _synced',
    plans: 'id',
    user_subscriptions: 'id, user_id, plan_id, is_active',
    user_addons: 'id, user_id, addon_id',
    addons: 'id',
    media_cache: 'media_key, cached_at'
}).upgrade(async trans => {
    // Migrate facility_charges -> facility_charges
    const facility_charges = await trans.facility_charges.toArray();
    if (facility_charges.length > 0) {
        await trans.facility_charges.bulkAdd(facility_charges);
    }

    // Migrate commission_rules -> fee_agreements
    const rules = await trans.commission_rules.toArray();
    if (rules.length > 0) {
        await trans.fee_agreements.bulkAdd(rules);
    }
});

// Version 16: Rename facility_charge fields: referral_doctor_id → clinic_id, treating_doctor_id → associated_doctor_id
// Also remove referral_doctor_id from fee_agreements indexes
db.version(16).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced',
    doctors: null,
    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, invoice_id, _synced',

    facility_charges: 'id, appointment_id, associated_doctor_id, clinic_id, paid_status, _synced',
    commissions: null,

    appointment_notes: 'id, appointment_id, doctor_id, type, upload_status, _synced',
    settings: 'user_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, user_id, [clinic_id+user_id], role, _synced',
    patient_doctor_links: 'id, patient_id, user_id, clinic_id, _synced',

    fee_agreements: 'id, user_id, patient_id, clinic_id, [user_id+patient_id], _synced',
    commission_rules: null,

    invoices: 'id, invoice_number, doctor_id, clinic_id, patient_id, status, _synced',
    plans: 'id',
    user_subscriptions: 'id, user_id, plan_id, is_active',
    user_addons: 'id, user_id, addon_id',
    addons: 'id',
    media_cache: 'media_key, cached_at'
}).upgrade(async trans => {
    // Remap facility_charges fields
    await trans.facility_charges.toCollection().modify(charge => {
        // referral_doctor_id was the payee → now clinic_id handles this (set by backend/sync)
        // treating_doctor_id was the payer → now associated_doctor_id
        if (charge.treating_doctor_id && !charge.associated_doctor_id) {
            charge.associated_doctor_id = charge.treating_doctor_id;
        }
        // Don't delete legacy fields here - sync will overwrite with clean data
    });

    // Clean up fee_agreements: remove referral_doctor_id if present
    await trans.fee_agreements.toCollection().modify(rule => {
        if (rule.referral_doctor_id) {
            delete rule.referral_doctor_id;
        }
    });
});

// Version 17: Support optional age/DOB and address for Patients
db.version(17).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced',
    doctors: null,
    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, invoice_id, _synced',

    facility_charges: 'id, appointment_id, associated_doctor_id, clinic_id, paid_status, _synced',
    commissions: null,

    appointment_notes: 'id, appointment_id, doctor_id, type, upload_status, _synced',
    settings: 'user_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, user_id, [clinic_id+user_id], role, _synced',
    patient_doctor_links: 'id, patient_id, user_id, clinic_id, _synced',

    fee_agreements: 'id, user_id, patient_id, clinic_id, [user_id+patient_id], _synced',
    commission_rules: null,

    invoices: 'id, invoice_number, doctor_id, clinic_id, patient_id, status, _synced',
    plans: 'id',
    user_subscriptions: 'id, user_id, plan_id, is_active',
    user_addons: 'id, user_id, addon_id',
    addons: 'id',
    media_cache: 'media_key, cached_at'
});

// Version 18: Add custom form templates, versions, assessments, revisions, and local drafts
db.version(18).stores({
    users: 'id, email, phone, created_by_user_id, type, _synced',
    doctors: null,
    patients: 'id, phone, name, _synced',
    appointments: 'id, doctor_id, patient_id, date_time, status, clinic_id, invoice_id, _synced',
    facility_charges: 'id, appointment_id, associated_doctor_id, clinic_id, paid_status, _synced',
    commissions: null,
    appointment_notes: 'id, appointment_id, doctor_id, type, upload_status, _synced',
    settings: 'user_id',
    sync_queue: '++id, type, entity, status, created_at, entity_id',
    clinics: 'id, owner_doctor_id, _synced',
    clinic_members: 'id, clinic_id, user_id, [clinic_id+user_id], role, _synced',
    patient_doctor_links: 'id, patient_id, user_id, clinic_id, _synced',
    fee_agreements: 'id, user_id, patient_id, clinic_id, [user_id+patient_id], _synced',
    commission_rules: null,
    invoices: 'id, invoice_number, doctor_id, clinic_id, patient_id, status, _synced',
    plans: 'id',
    user_subscriptions: 'id, user_id, plan_id, is_active',
    user_addons: 'id, user_id, addon_id',
    addons: 'id',
    media_cache: 'media_key, cached_at',
    form_templates: 'id, owner_type, owner_id, active_version_id, status, _synced',
    form_template_versions: 'id, template_id, status, version_number, _synced',
    form_assessments: 'id, appointment_id, template_id, template_version_id, author_id, clinic_id, status, sync_status, _synced',
    form_assessment_revisions: 'id, assessment_id, revision_number, submitted_by, _synced',
    form_assessment_drafts: 'id, appointment_id, template_version_id, assessment_id, author_id, kind, state, updated_at'
});


// Media cache constants
export const MEDIA_CACHE_MAX_SIZE = 200 * 1024 * 1024; // 200MB max cache size
export const MEDIA_CACHE_MAX_ITEM_SIZE = 100 * 1024 * 1024; // 100MB max single item (videos)

/**
 * Cache media blob in IndexedDB with LRU eviction
 */
export async function cacheMedia(mediaKey, blob, contentType) {
    if (!mediaKey || !blob) return;

    const sizeBytes = blob.size;

    // Don't cache items larger than max item size
    if (sizeBytes > MEDIA_CACHE_MAX_ITEM_SIZE) {
        console.log(`Media too large to cache: ${sizeBytes} bytes`);
        return;
    }

    try {
        // Calculate current cache size
        const allCached = await db.media_cache.toArray();
        let currentSize = allCached.reduce((sum, item) => sum + (item.size_bytes || 0), 0);

        // Evict oldest items until we have space
        while (currentSize + sizeBytes > MEDIA_CACHE_MAX_SIZE && allCached.length > 0) {
            // Sort by cached_at (oldest first)
            allCached.sort((a, b) => new Date(a.cached_at) - new Date(b.cached_at));
            const oldest = allCached.shift();
            if (oldest) {
                await db.media_cache.delete(oldest.media_key);
                currentSize -= oldest.size_bytes || 0;
                console.log(`Evicted from cache: ${oldest.media_key}`);
            }
        }

        // Add to cache
        await db.media_cache.put({
            media_key: mediaKey,
            blob: blob,
            size_bytes: sizeBytes,
            content_type: contentType,
            cached_at: new Date().toISOString()
        });

        console.log(`Cached media: ${mediaKey} (${(sizeBytes / 1024 / 1024).toFixed(2)}MB)`);
    } catch (error) {
        console.error('Failed to cache media:', error);
    }
}

/**
 * Get cached media blob from IndexedDB
 */
export async function getCachedMedia(mediaKey) {
    if (!mediaKey) return null;

    try {
        const cached = await db.media_cache.get(mediaKey);
        if (cached?.blob) {
            // Update cached_at to implement LRU
            await db.media_cache.update(mediaKey, {
                cached_at: new Date().toISOString()
            });
            return cached.blob;
        }
    } catch (error) {
        console.error('Failed to get cached media:', error);
    }
    return null;
}

/**
 * Clear all media cache (call on logout)
 */
export async function clearMediaCache() {
    try {
        await db.media_cache.clear();
        console.log('Media cache cleared');
    } catch (error) {
        console.error('Failed to clear media cache:', error);
    }
}
